/**
 * 「照着念的那一份文本」—— 把稿子里的**时间轴标记**去掉。
 *
 * ## 它守的是哪一件事
 *
 * 2026-10-09 用户报障（真机截图）：「**怎么把时间轴都跟着读了，你搞笑吧**」。
 * 口播稿长这样（`_FILL_SYSTEM['human']` 就是这么要求模型的）：
 *
 * ```
 * 0-3s: 每个月电费，是不是总让你皱眉？
 * 3-6s: 看这个电费煲，名字就直白。
 * ```
 *
 * 而配音那一格把 `form.script` 整段递给了 TTS —— 于是「0-3s:」被**念了出来**，
 * 用户听到的开头是「零到三秒 每个月电费…」。
 *
 * 🔴 **时间轴不是给人念的，是给人看的**：它标的是「这一段占第几秒」，
 * 是稿件结构的一部分（分镜图、成片时长都按它对齐）。所以正确的做法是
 * **稿子照旧带时间轴、念的时候剥掉**，而不是让 AI 别写时间轴 ——
 * 后者等于把「每段几秒」这个信息从界面上抹掉。
 *
 * ## 三条口径（都对应一处真会犯的错）
 *
 * ① **它是派生值，不是第二份真源。** `form.script` 仍是唯一真源（用户看得见、
 *    改得动的那一份），`spokenText(form.script)` 每次现算。**不许**在草稿里
 *    另存一个 `scriptSpoken` 字段 —— 用户手改稿子时两处必然不同步，
 *    而症状是「念的和他看见的不是同一份」，本项目最难查的那一类。
 *
 * ② **只剥「时间轴形态」的东西，不动别的一个字。** 判据窄到「行首/任意位置的
 *    `数字-数字` + `s/秒` + `冒号`」：`3-6秒速热：` 这种说法不会被误伤
 *    （`秒` 后面必须紧跟冒号才算标记），产品名里的「24」更不受影响。
 *    剥离后**不补任何字**（连「（以下为口播稿）」这种过渡语都不许加）——
 *    加一个字就是替用户改台词。
 *
 * ③ **只归「稿子格式由我们规定」的地方用。** 现在只有 `/digital-human`
 *    的口播稿（AI 按时间轴格式生成，见 `_FILL_SYSTEM['human']`）。
 *    `/tts` 页**刻意不用它**：那里的输入框写的就是「**这就是会被念出来的全文**」
 *    （`TtsStudio.tsx` 头注），用户在自由文本里打什么就该念什么 ——
 *    在后端或全局偷偷剥掉，等于静默改写用户输入，那是另一个更重的错。
 *
 * ## 为什么不放后端
 *
 * 后端剥也能治标，但：`/tts` 页会被连坐（自由输入被静默改写）；
 * 而且「界面上显示的稿子」与「实际念的文本」的差异就再没有一处说得清 ——
 * 用户看到带时间轴的稿子、听到不带时间轴的音频，中间那一步得能在界面上指出来
 * （见配音卡那行 hint）。派生在前端算，界面才拿得到同一份值去解释自己。
 */
/**
 * 时间轴标记的形态。三条都被用例钉住（`scripts/smoke-spoken.ts`）：
 *
 * · **空白只许吃行内的空格/制表符（`[ \t]`），绝不许用 `\s`** ——
 *   `\s` 会连**换行**一起吃掉，段与段就会粘成一行
 *   （`…皱眉？\n3-6s: 看…` → `…皱眉？看…`）。这个坑真踩过：
 *   第一版用的是 `\s*`，五个时间段被压成一段话 —— 不报错、听着还像连贯的，
 *   只是每段之间的停顿没了、分镜与配音对不上。
 * · **区间是可选的**：末段常写成单个时间点（`15s：点下方链接…`），
 *   只认 `0-3s` 会漏掉它，而漏掉的那一段会被原样念出来。
 * · **包裹符号（`【】`/`（）`）可有可无**，模型两种都写过。
 */
const TIMELINE_MARK =
  /[【（(\[]?[ \t]*(?:第[ \t]*)?\d+(?:\.\d+)?(?:[ \t]*(?:[-–—~～]|至|到)[ \t]*\d+(?:\.\d+)?)?[ \t]*(?:s|S|秒(?:钟)?)[ \t]*[】）)\]]?[ \t]*[:：][ \t]*/g

/**
 * 取出「会被念出来的那一份」。
 *
 * 覆盖 `0-3s:` / `0-3s：` / `0–3s：` / `0~3s：` / `0 至 3 秒：` / `第 0-3 秒：`
 * / `【0-3s】：`，全半角冒号都认。**行首与行内都剥** —— 行内那支是防
 * 「AI 没按 `\n` 分行、把五段挤成一行」（提示词要求分行，但那是概率不是保证）。
 */
export function spokenText(raw: string): string {
  return raw
    .replace(TIMELINE_MARK, '')
    .replace(/[ \t]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}
